/**
 * The app palette, built from the colour tokens generated for the base colour (tokens/palette.js).
 * Material Dashboard keys (info, dark, gradients, ...) stay, so MD components follow the tokens.
 */

const socialMediaColors = {
  facebook: { main: "#3b5998", dark: "#344e86" },
  twitter: { main: "#55acee", dark: "#3ea1ec" },
  instagram: { main: "#125688", dark: "#0e456d" },
  linkedin: { main: "#0077b5", dark: "#00669c" },
  pinterest: { main: "#cc2127", dark: "#b21d22" },
  youtube: { main: "#e52d27", dark: "#d41f1a" },
  vimeo: { main: "#1ab7ea", dark: "#13a3d2" },
  slack: { main: "#3aaf85", dark: "#329874" },
  dribbble: { main: "#ea4c89", dark: "#e73177" },
  github: { main: "#24292e", dark: "#171a1d" },
  reddit: { main: "#ff4500", dark: "#e03d00" },
  tumblr: { main: "#35465c", dark: "#2a3749" },
};

// Gradients are flat fills: the reference design has none.
const flat = (color) => ({ main: color, state: color });

/**
 * MUI + Material Dashboard palette for one theme. `tokens` is the full buildTokens() result:
 * fills that MD components pair with white text keep the light-theme colours in both modes.
 * @since NEXT_VERSION
 */
export function makeColors(tokens, mode = "light") {
  const t = tokens[mode];
  const fill = tokens.light;
  const dark = mode === "dark";
  const accent = {
    main: t.accent,
    focus: t["accent-hover"],
    light: dark ? t.c1 : t.c3,
    dark: t["accent-hover"],
    contrastText: t["on-accent"],
  };
  const status = (name) => ({
    main: t[name],
    focus: t[`${name}-hover`],
    light: t[`${name}-tint`],
    dark: t[`${name}-hover`],
    contrastText: t[`on-${name}`],
  });

  return {
    background: { default: t.bg, paper: t.surface, card: t.surface, sidenav: t.ink },
    text: {
      main: t["text-2"],
      focus: t.text,
      primary: t.text,
      secondary: t["text-2"],
      disabled: t["text-3"],
    },
    divider: t.border,
    // Read by MUI X DataGrid: a tinted header row over the plain surface (components/dataGrid.js).
    DataGrid: { bg: t.surface, headerBg: t["surface-2"], pinnedBg: t.surface },
    transparent: { main: "rgba(0, 0, 0, 0)" },
    white: { main: "#ffffff", focus: "#ffffff" },
    black: { light: "#000000", main: "#000000", focus: "#000000" },
    primary: accent,
    secondary: { main: t["text-3"], focus: t["text-2"], contrastText: dark ? t.ink : t["on-ink"] },
    info: accent,
    success: status("success"),
    warning: status("warning"),
    error: status("error"),
    light: { main: t["surface-3"], focus: t["surface-3"] },
    dark: { main: t.ink, focus: t["ink-raise"] },
    // Light-to-dark in light mode; in dark mode the steps the overrides use for text turn light.
    grey: {
      100: t["surface-2"],
      200: t["surface-3"],
      300: t.border,
      400: t["border-strong"],
      500: t["field-border"],
      600: t["text-3"],
      700: t["text-2"],
      800: dark ? t.text : t["ink-raise"],
      900: dark ? t.text : t.ink,
    },
    gradients: {
      primary: flat(fill.accent),
      secondary: flat(fill["text-3"]),
      info: flat(fill.accent),
      success: flat(fill.success),
      warning: flat(fill.warning),
      error: flat(fill.error),
      light: flat(t["surface-3"]),
      dark: flat(t.ink),
    },
    socialMediaColors,
    badgeColors: {
      primary: { background: t["accent-soft-2"], text: t["accent-strong"] },
      secondary: { background: t["surface-3"], text: t["text-2"] },
      info: { background: t["accent-soft-2"], text: t["accent-strong"] },
      success: { background: t["success-soft-2"], text: t["success-strong"] },
      warning: { background: t["warning-soft-2"], text: t["warning-strong"] },
      error: { background: t["error-soft-2"], text: t["error-strong"] },
      light: { background: t.surface, text: t["text-3"] },
      dark: { background: t["ink-line"], text: t["on-ink"] },
    },
    coloredShadows: {
      primary: fill.accent,
      secondary: t["text-3"],
      info: fill.accent,
      success: fill.success,
      warning: fill.warning,
      error: fill.error,
      light: t["border-strong"],
      dark: t.ink,
    },
    inputBorderColor: t["field-border"],
    tabs: { indicator: { boxShadow: t.border } },
  };
}
